import {
  createContext,
  ReactNode,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from "react";
import { useParams } from "react-router";
import { getMedicalClaimFileUnit } from "Requests/Partners/PreBudgets";
import {
  ProductWithSource,
  TrackItem,
  NotFoundProduct,
  NewTrackingModel,
  TrackingItem,
  AddedProduct,
  IAResponse,
  ProductFromAPI,
} from "./interface";

import { usePreOrderDetails } from "./hooks/usePreOrderDetails";

// Interface para resultado individual da IA
export interface IAResult {
  nome_original: string;
  nome_encontrado: string | null;
  id: number | null;
  encontrado: boolean;
  metodo: string | null;
  confianca: number;
  price: number | null;
}

interface ItemsProviderProps {
  items: ProductWithSource[];
  addItem: (product: ProductWithSource) => void;
  addQuantityItem: (productId: number) => void;
  deleteQuantityItem: (productId: number) => void;
  clearCart: () => void;
  deleteItem: (productId: number) => void;
  getTotalPrice: () => number;
  // Funções para tracking antigo (compatibilidade)
  replaceItem: (
    originalProductId: number,
    newProducts: ProductWithSource[]
  ) => void;
  getTrackHistory: () => TrackItem[];
  addTrackItem: (trackItem: TrackItem) => void;
  getTrackHistoryForExport: () => TrackItem[];
  resetTrackHistory: () => void;
  // Funções para novo modelo de tracking
  getNewTrackHistory: () => NewTrackingModel | null;
  initializeNewTracking: (iaResponse: IAResponse) => void;
  updateItemStatus: (
    originalProductId: number,
    status: "replaced" | "removed",
    finalProducts?: ProductWithSource[]
  ) => void;
  addManualProduct: (product: ProductWithSource) => void;
  // Produtos não encontrados da IA
  notFoundProducts: NotFoundProduct[];
  clearNotFoundProducts: () => void;
  // Adicionar arquivos ao contexto
  fileBlobs: { [filePath: string]: Blob };
  isLoadingFiles: boolean;
}

export const ItemsContext = createContext<ItemsProviderProps | null>(null);

export const ItemsProvider = ({
  children,
  iaResponse,
  productsResponse,
}: {
  children: ReactNode;
  iaResponse?: IAResponse | null;
  productsResponse?: {
    products: ProductFromAPI[];
    missingProductIds: number[];
    allProductsFound: boolean;
  };
}) => {
  const { id } = useParams<{ id: string }>();
  const preOderDetailsAPI = usePreOrderDetails(Number(id));
  const { products } = preOderDetailsAPI.data ?? {};

  const [items, setItems] = useState<ProductWithSource[]>([]);
  const [trackHistory, setTrackHistory] = useState<TrackItem[]>([]);
  const [newTrackingModel, setNewTrackingModel] =
    useState<NewTrackingModel | null>(null);
  const [notFoundProducts, setNotFoundProducts] = useState<NotFoundProduct[]>(
    []
  );
  const [fileBlobs, setFileBlobs] = useState<{ [filePath: string]: Blob }>({});
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);

  // Carregar produtos existentes do pre-budget quando a API retornar os dados
  useEffect(() => {
    if (
      preOderDetailsAPI.data?.products &&
      preOderDetailsAPI.data.products.length > 0
    ) {
      const existingProducts: ProductWithSource[] =
        preOderDetailsAPI.data.products.map((product) => ({
          product_id: product.product_id,
          product_name: product.product,
          product_code: product.sku,
          amount: product.amount || 1,
          product_price: product.price ? Number(product.price) : 0,
          source: (product.source as "ia" | "manual") || "manual", // Produtos existentes no pre-budget são considerados manuais
          // Não há originalIAProduct pois não foram adicionados via IA
        }));

      setItems(existingProducts);
    }
  }, [products, preOderDetailsAPI.data]);

  // Carregar track da API quando disponível
  useEffect(() => {
    if (preOderDetailsAPI.data?.track) {
      const apiTrack = preOderDetailsAPI.data.track;

      // Verificar se é o novo formato ou formato antigo
      // Se apiTrack tem propriedades do novo formato, é o novo modelo
      if (
        apiTrack &&
        typeof apiTrack === "object" &&
        "exams_normalized" in apiTrack &&
        "items" in apiTrack
      ) {
        // Novo formato - carregar diretamente
        setNewTrackingModel(apiTrack as unknown as NewTrackingModel);
      } else if (Array.isArray(apiTrack)) {
        // Formato antigo - converter para formato local
        const localTrack = apiTrack.map((trackItem) => ({
          produto_original: {
            produto_original_nome:
              trackItem.produto_original.produto_original_nome,
            produto_original_match_nome:
              trackItem.produto_original.produto_original_match_nome,
            produto_original_match_id:
              trackItem.produto_original.produto_original_match_id,
          },
          items_corretos: trackItem.items_corretos.map((item) => ({
            produto_corrigido_nome: item.produto_corrigido_nome,
            produto_corrigido_id: Number(item.produto_corrigido_id), // Converter para number
          })),
        }));

        setTrackHistory(localTrack);
      }
    }
  }, [preOderDetailsAPI.data?.track]);

  // Carregar produtos não encontrados da IA + rota de produtos
  useEffect(() => {
    if (iaResponse?.matching_results?.resultados && productsResponse) {
      // 1. Produtos que a IA não conseguiu encontrar (sem ID)
      const iaNotFound = iaResponse.matching_results.resultados
        .filter((r: IAResult) => !r.encontrado)
        .map((r: IAResult) => ({
          id: null,
          name: r.nome_original,
        }));

      // 2. Produtos que a IA encontrou mas não vieram da rota de produtos (com ID)
      const iaFoundButNotInRoute = iaResponse.matching_results.resultados
        .filter((r: IAResult) => {
          return (
            !productsResponse.products.some(
              (p: ProductFromAPI) => p.id === r.id
            ) && r.encontrado
          );
        })
        .map((r: IAResult) => ({
          id: r.id,
          name: r.nome_encontrado || r.nome_original,
        }));

      // 3. Produtos que vieram da rota mas não têm partners (não estão disponíveis para compra)
      const productsWithoutPartners = productsResponse.products
        .filter((p: ProductFromAPI) => !p.partners || p.partners.length === 0)
        .map((p: ProductFromAPI) => ({
          id: p.id,
          name: p.product || "Produto sem nome",
        }));

      // 4. Combinar todas as listas
      const allNotFound = [
        ...iaNotFound,
        ...iaFoundButNotInRoute,
        ...productsWithoutPartners,
      ];

      setNotFoundProducts(allNotFound);
    }
  }, [iaResponse?.matching_results, productsResponse]);

  // Carregar todos os arquivos de uma vez quando a API retornar os dados
  useEffect(() => {
    const loadAllFiles = async () => {
      const orderFiles = preOderDetailsAPI.data?.order_files || [];
      const healthFiles = preOderDetailsAPI.data?.health_files || [];
      const allFiles = [...orderFiles, ...healthFiles];

      if (allFiles && allFiles.length > 0) {
        setIsLoadingFiles(true);
        try {
          const filePromises = allFiles.map(async (file) => {
            const blob = await getMedicalClaimFileUnit(file.file_path);
            return { filePath: file.file_path, blob };
          });

          const fileResults = await Promise.all(filePromises);
          const fileBlobsMap = fileResults.reduce((acc, { filePath, blob }) => {
            acc[filePath] = blob;
            return acc;
          }, {} as { [filePath: string]: Blob });

          setFileBlobs(fileBlobsMap);
        } catch (error) {
          console.error("Erro ao carregar arquivos:", error);
        } finally {
          setIsLoadingFiles(false);
        }
      }
    };

    loadAllFiles();
  }, [
    preOderDetailsAPI.data?.order_files,
    preOderDetailsAPI.data?.health_files,
  ]);

  // Memoizar funções para evitar recriações desnecessárias
  const addItem = useCallback((product: ProductWithSource) => {
    setItems((prev) => {
      const existingItem = prev.find(
        (item) => item.product_id === product.product_id
      );

      if (existingItem) {
        return prev.map((item) =>
          item.product_id === product.product_id
            ? { ...item, amount: item.amount + 1 }
            : item
        );
      }
      return [...prev, product];
    });
  }, []);

  const addQuantityItem = useCallback((productId: number) => {
    setItems((prev) =>
      prev.map((item) =>
        item.product_id === productId
          ? { ...item, amount: item.amount + 1 }
          : item
      )
    );
  }, []);

  const deleteQuantityItem = useCallback((productId: number) => {
    setItems((prev) =>
      prev.map((item) =>
        item.product_id === productId && item.amount > 1
          ? { ...item, amount: item.amount - 1 }
          : item
      )
    );
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setTrackHistory([]); // Resetar também o track ao limpar o carrinho
    setNotFoundProducts([]); // Resetar também os produtos não encontrados
  }, []);

  // Memoizar o cálculo do preço total
  const getTotalPrice = useCallback(() => {
    return items.reduce(
      (total, item) => total + item.product_price * item.amount,
      0
    );
  }, [items]);

  // NOVAS FUNÇÕES PARA TRACKING E SUBSTITUIÇÃO

  const addTrackItem = useCallback((trackItem: TrackItem) => {
    setTrackHistory((prev) => [...prev, trackItem]);
  }, []);

  const getTrackHistory = useCallback(() => {
    return trackHistory;
  }, [trackHistory]);

  const getTrackHistoryForExport = useCallback(() => {
    return trackHistory;
  }, [trackHistory]);

  // Função para resetar o track (usado quando "Gerar com IA" é executado)
  const resetTrackHistory = useCallback(() => {
    setTrackHistory([]);
    setNewTrackingModel(null); // Resetar também o novo modelo
    setNotFoundProducts([]); // Resetar também os produtos não encontrados
  }, []);

  // NOVO MODELO DE TRACKING - Função de inicialização
  const initializeNewTracking = useCallback((iaResponseData: IAResponse) => {
    if (
      !iaResponseData?.matching_results?.resultados ||
      !iaResponseData?.consolidated_analysis?.ai_analysis?.examType
    ) {
      return;
    }

    const { resultados } = iaResponseData.matching_results;
    const { examType } = iaResponseData.consolidated_analysis.ai_analysis;

    // 1. Criar exams_normalized a partir do examType
    const exams_normalized = Array.isArray(examType) ? examType : [];

    // 2. Criar items com status "kept" para produtos encontrados
    const trackingItems: TrackingItem[] = resultados
      .filter((result: IAResult) => result.encontrado && result.id)
      .map((result: IAResult) => ({
        original_product: {
          original_name: result.nome_original,
          found_name: result.nome_encontrado || result.nome_original,
          id: result.id as number, // Garantir que não é null
          details: {
            method: result.metodo || "unknown",
            confidence: result.confianca || 0,
          },
        },
        final_status: "kept" as const,
      }));

    // 3. Criar not_founded para produtos não encontrados
    const not_founded = resultados
      .filter((result: IAResult) => !result.encontrado)
      .map((result: IAResult) => result.nome_original);

    // 4. Inicializar added_products vazio (será preenchido conforme usuário adiciona)
    const added_products: AddedProduct[] = [];

    const newTrackingModelData: NewTrackingModel = {
      exams_normalized,
      items: trackingItems,
      added_products,
      not_founded,
    };

    setNewTrackingModel(newTrackingModelData);
  }, []);

  // Função para obter o novo tracking
  const getNewTrackHistory = useCallback(() => {
    return newTrackingModel;
  }, [newTrackingModel]);

  // Função para atualizar status de um item
  const updateItemStatus = useCallback(
    (
      originalProductId: number,
      status: "replaced" | "removed",
      finalProducts?: ProductWithSource[]
    ) => {
      setNewTrackingModel((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          items: prev.items.map((item) => {
            if (item.original_product.id === originalProductId) {
              const updatedItem: TrackingItem = {
                ...item,
                final_status: status,
              };

              // Se for substituição, adicionar final_products
              if (status === "replaced" && finalProducts) {
                updatedItem.final_products = finalProducts.map((product) => ({
                  id: product.product_id,
                  name: product.product_name,
                }));
              }

              return updatedItem;
            }
            return item;
          }),
        };
      });
    },
    []
  );

  // Função para adicionar produto manual
  const addManualProduct = useCallback((product: ProductWithSource) => {
    setNewTrackingModel((prev) => {
      // Se não existe newTrackingModel, criar um básico para produtos manuais
      if (!prev) {
        const newAddedProduct: AddedProduct = {
          id: product.product_id,
          name: product.product_name,
        };

        return {
          exams_normalized: [],
          items: [],
          added_products: [newAddedProduct],
          not_founded: [],
        };
      }

      const newAddedProduct: AddedProduct = {
        id: product.product_id,
        name: product.product_name,
      };

      // Verificar se o produto já existe na lista para evitar duplicados
      const productExists = prev.added_products.some(
        (existingProduct) => existingProduct.id === product.product_id
      );

      if (productExists) {
        // Produto já existe, não adicionar duplicado
        return prev;
      }

      return {
        ...prev,
        added_products: [...prev.added_products, newAddedProduct],
      };
    });
  }, []);

  // Redefinir funções com integração ao novo tracking
  const deleteItemWithTracking = useCallback(
    (productId: number) => {
      setItems((prev) => {
        const itemToDelete = prev.find((item) => item.product_id === productId);

        if (itemToDelete) {
          // Atualizar status no novo modelo de tracking
          if (itemToDelete.source === "ia") {
            updateItemStatus(productId, "removed");
          } else if (itemToDelete.source === "manual") {
            // Remover produto manual da lista added_products
            setNewTrackingModel((prevModel) => {
              if (!prevModel) return prevModel;

              return {
                ...prevModel,
                added_products: prevModel.added_products.filter(
                  (addedProduct) => addedProduct.id !== productId
                ),
              };
            });
          }

          // Adicionar ao tracking (modelo antigo - compatibilidade)
          if (itemToDelete.source === "ia" && itemToDelete.originalIAProduct) {
            const trackItem: TrackItem = {
              produto_original: {
                produto_original_nome:
                  itemToDelete.originalIAProduct.nome_enviado,
                produto_original_match_nome:
                  itemToDelete.originalIAProduct.nome_encontrado,
                produto_original_match_id:
                  itemToDelete.originalIAProduct.id_encontrado,
              },
              items_corretos: [],
            };
            setTrackHistory((prevTrack) => [...prevTrack, trackItem]);
          }
        }

        return prev.filter((item) => item.product_id !== productId);
      });
    },
    [updateItemStatus]
  );

  const replaceItemWithTracking = useCallback(
    (originalProductId: number, newProducts: ProductWithSource[]) => {
      setItems((prev) => {
        const originalItem = prev.find(
          (item) => item.product_id === originalProductId
        );

        if (!originalItem) return prev;

        // Atualizar status no novo modelo de tracking
        if (originalItem.source === "ia") {
          updateItemStatus(originalProductId, "replaced", newProducts);
        }

        // Criar item de tracking (modelo antigo - compatibilidade)
        if (originalItem.source === "ia" && originalItem.originalIAProduct) {
          const trackItem: TrackItem = {
            produto_original: {
              produto_original_nome:
                originalItem.originalIAProduct.nome_enviado,
              produto_original_match_nome:
                originalItem.originalIAProduct.nome_encontrado,
              produto_original_match_id:
                originalItem.originalIAProduct.id_encontrado,
            },
            items_corretos: newProducts.map((product) => ({
              produto_corrigido_nome: product.product_name,
              produto_corrigido_id: product.product_id,
            })),
          };
          setTrackHistory((prevTrack) => [...prevTrack, trackItem]);
        }

        // Remover produto original e adicionar novos produtos
        const filteredItems = prev.filter(
          (item) => item.product_id !== originalProductId
        );
        return [...filteredItems, ...newProducts];
      });
    },
    [updateItemStatus]
  );

  const clearNotFoundProducts = useCallback(() => {
    setNotFoundProducts([]);
  }, []);

  // Memoizar o objeto de valores para evitar recriações desnecessárias
  const values = useMemo(
    () => ({
      items,
      addItem,
      addQuantityItem,
      deleteQuantityItem,
      clearCart,
      deleteItem: deleteItemWithTracking, // Usar versão com tracking
      getTotalPrice,
      // Funções para tracking antigo (compatibilidade)
      replaceItem: replaceItemWithTracking, // Usar versão com tracking
      getTrackHistory,
      addTrackItem,
      getTrackHistoryForExport,
      resetTrackHistory,
      // Funções para novo modelo de tracking
      getNewTrackHistory,
      initializeNewTracking,
      updateItemStatus,
      addManualProduct,
      // Outros
      notFoundProducts,
      clearNotFoundProducts,
      fileBlobs,
      isLoadingFiles,
    }),
    [
      items,
      trackHistory,
      newTrackingModel,
      addItem,
      addQuantityItem,
      deleteQuantityItem,
      clearCart,
      deleteItemWithTracking,
      getTotalPrice,
      replaceItemWithTracking,
      getTrackHistory,
      addTrackItem,
      getTrackHistoryForExport,
      resetTrackHistory,
      getNewTrackHistory,
      initializeNewTracking,
      updateItemStatus,
      addManualProduct,
      notFoundProducts,
      clearNotFoundProducts,
      fileBlobs,
      isLoadingFiles,
    ]
  );

  return (
    <ItemsContext.Provider value={values}>{children}</ItemsContext.Provider>
  );
};
